import { useMemo } from 'react';
import { createIntl, createIntlCache, useIntl } from 'gatsby-plugin-react-intl';
import { renderToStaticMarkup } from 'react-dom/server';
import type CookieConsent from 'vanilla-cookieconsent';

import { translations, type Language } from '@workspace/localization';

import { siteConfig as appConfig } from '~config';

import { mapLocaleToCcLang } from '../utils/localeMap';

const cache = createIntlCache();

const buildTranslation = (lang: Language): CookieConsent.Translation => {
    const intl = createIntl({ locale: lang, messages: translations[lang] }, cache);
    const cookiePolicyHref = `/${lang}${appConfig.pages.cookiePolicy}`;
    const cookiePolicyLink = renderToStaticMarkup(
        <a href={cookiePolicyHref} className='cc__link'>
            {intl.formatMessage({ id: 'cookieConsent.cookiePolicyLinkLabel' })}
        </a>,
    );

    return {
        consentModal: {
            title: intl.formatMessage({ id: 'cookieConsent.banner.title' }),
            description: intl.formatMessage(
                { id: 'cookieConsent.banner.description' },
                { privacyPolicyLink: cookiePolicyLink },
            ),
            acceptAllBtn: intl.formatMessage({ id: 'cookieConsent.banner.acceptAll' }),
            acceptNecessaryBtn: intl.formatMessage({ id: 'cookieConsent.banner.acceptNecessary' }),
            showPreferencesBtn: intl.formatMessage({ id: 'cookieConsent.banner.showPreferences' }),
        },
        preferencesModal: {
            title: intl.formatMessage({ id: 'cookieConsent.preferences.title' }),
            acceptAllBtn: intl.formatMessage({ id: 'cookieConsent.preferences.acceptAll' }),
            acceptNecessaryBtn: intl.formatMessage({ id: 'cookieConsent.preferences.acceptNecessary' }),
            savePreferencesBtn: intl.formatMessage({ id: 'cookieConsent.preferences.savePreferences' }),
            closeIconLabel: intl.formatMessage({ id: 'cookieConsent.preferences.close' }),
            sections: [
                {
                    title: intl.formatMessage({ id: 'cookieConsent.preferences.sectionsTitle' }),
                    description: intl.formatMessage(
                        { id: 'cookieConsent.preferences.description' },
                        { privacyPolicyLink: cookiePolicyLink },
                    ),
                },
                {
                    title: intl.formatMessage({ id: 'cookieConsent.categories.necessary.title' }),
                    description: intl.formatMessage({ id: 'cookieConsent.categories.necessary.description' }),
                    linkedCategory: 'necessary',
                },
                {
                    title: intl.formatMessage({ id: 'cookieConsent.categories.functional.title' }),
                    description: intl.formatMessage({ id: 'cookieConsent.categories.functional.description' }),
                    linkedCategory: 'functional',
                },
                {
                    title: intl.formatMessage({ id: 'cookieConsent.categories.analytics.title' }),
                    description: intl.formatMessage({ id: 'cookieConsent.categories.analytics.description' }),
                    linkedCategory: 'analytics',
                },
                {
                    title: intl.formatMessage({ id: 'cookieConsent.categories.marketing.title' }),
                    description: intl.formatMessage({ id: 'cookieConsent.categories.marketing.description' }),
                    linkedCategory: 'marketing',
                },
            ],
        },
    };
};

const allTranslations: Record<string, CookieConsent.Translation> = Object.fromEntries(
    (Object.keys(translations) as Language[]).map(lang => [lang, buildTranslation(lang)]),
);

export const useCookieConsentConfig = (): CookieConsent.CookieConsentConfig => {
    const intl = useIntl();
    const ccLang = mapLocaleToCcLang(intl.locale);

    return useMemo<CookieConsent.CookieConsentConfig>(
        () => ({
            guiOptions: {
                consentModal: {
                    layout: 'cloud inline',
                    position: 'bottom left',
                    equalWeightButtons: true,
                    flipButtons: false,
                },
                preferencesModal: {
                    layout: 'box',
                    position: 'right',
                    equalWeightButtons: true,
                    flipButtons: false,
                },
            },
            categories: {
                necessary: {
                    enabled: true,
                    readOnly: true,
                },
                functional: {
                    autoClear: { cookies: [], reloadPage: true },
                },
                analytics: {
                    autoClear: {
                        cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: /^_gat/ }],
                        reloadPage: true,
                    },
                },
                marketing: {
                    autoClear: {
                        cookies: [
                            { name: 'passwd_utm_original' },
                            { name: 'passwd_utm_final' },
                            { name: /^trakdesk/ },
                            { name: /^_gcl/ },
                        ],
                        reloadPage: true,
                    },
                },
            },
            language: {
                default: ccLang,
                translations: allTranslations,
            },
        }),
        [ccLang],
    );
};
